// src/components/RegistrationHistoryModal.tsx
//
// Read-only modal showing every audit entry for a single registration.
// Triggered from the admin ViewRegistrations page (per-athlete "History"
// button). Each entry lists which fields moved and by whom.

import React, { useEffect, useState } from "react";
import {
  getAuditLogByRegistration,
  RegistrationAuditEntry,
} from "../firebase/auditLog";
import { Registration } from "../firebase/registrations";
import { C, FONT } from "../theme";

interface Props {
  registration: Registration & { id: string };
  onClose: () => void;
}

function formatTimestamp(ts: any): string {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Human-readable field label for an audit-tracked field. */
const FIELD_LABELS: Record<string, string> = {
  weightCategory: "Weight",
  division: "Division",
  category: "Category",
  club: "Club",
  price: "Price",
};

function renderValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v);
  return String(v);
}

export default function RegistrationHistoryModal({ registration, onClose }: Props) {
  const [entries, setEntries] = useState<RegistrationAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const log = await getAuditLogByRegistration(registration.id);
        if (!cancelled) setEntries(log);
      } catch (err) {
        console.error("Failed to load audit log:", err);
        if (!cancelled) setError("Failed to load history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [registration.id]);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.label}>EDIT HISTORY</div>
            <div style={s.title}>
              {registration.athleteSnapshot.firstName}{" "}
              {registration.athleteSnapshot.lastName}
            </div>
            <div style={s.subtitle}>
              Ref: <span style={s.refCode}>{registration.referenceCode}</span>
              {" · "}
              <span style={{ color: C.text3 }}>
                {registration.competitionName}
              </span>
            </div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.body}>
          {loading && <div style={s.stateMsg}>Loading history…</div>}
          {error && <div style={s.errorBox}>{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div style={s.empty}>
              <div style={s.emptyIcon}>📋</div>
              <div style={s.emptyText}>No edits recorded yet.</div>
              <div style={s.emptySub}>
                Changes made by coaches or admins will appear here.
              </div>
            </div>
          )}

          {!loading && !error && entries.length > 0 && (
            <div style={s.timeline}>
              {entries.map((entry) => (
                <div key={entry.id} style={s.entry}>
                  <div style={s.entryHeader}>
                    <span
                      style={
                        entry.changedBy === "admin"
                          ? s.badgeAdmin
                          : s.badgeCoach
                      }
                    >
                      {entry.changedBy === "admin" ? "ADMIN" : "COACH"}
                    </span>
                    {entry.action === "deleted" && (
                      <span style={s.badgeDeleted}>REMOVED</span>
                    )}
                    <span style={s.entryEmail}>{entry.editorEmail || "—"}</span>
                    <span style={s.entryTime}>
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                  <div style={s.changes}>
                    {Object.entries(entry.changes).map(([field, diff]) => (
                      <div key={field} style={s.changeRow}>
                        <span style={s.fieldName}>
                          {FIELD_LABELS[field] || field}
                        </span>
                        <span style={s.fromValue}>{renderValue(diff.from)}</span>
                        <span style={s.arrow}>→</span>
                        <span style={s.toValue}>{renderValue(diff.to)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "14px",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "22px 24px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  label: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "4px",
  },
  title: {
    fontFamily: FONT.display,
    fontSize: "22px",
    color: C.text,
    letterSpacing: "0.5px",
    lineHeight: 1.1,
    marginBottom: "4px",
  },
  subtitle: { fontSize: "12px", color: C.text3 },
  refCode: {
    fontFamily: "monospace",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "4px",
    padding: "1px 6px",
    color: C.text,
    fontWeight: 600,
  },
  closeBtn: {
    background: "none",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    color: C.text3,
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px 8px",
    lineHeight: 1,
    flexShrink: 0,
  },
  body: { padding: "20px 24px 24px" },
  stateMsg: { textAlign: "center", padding: "40px 20px", color: C.text2, fontSize: "13px" },
  errorBox: {
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    color: C.red,
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "13px",
  },
  empty: { textAlign: "center", padding: "40px 20px" },
  emptyIcon: { fontSize: "32px", marginBottom: "10px" },
  emptyText: { fontSize: "14px", color: C.text2, marginBottom: "6px" },
  emptySub: { fontSize: "12px", color: C.text3 },
  timeline: { display: "flex", flexDirection: "column", gap: "12px" },
  entry: {
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
  },
  entryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  badgeAdmin: {
    backgroundColor: "var(--color-warning-soft)",
    color: "var(--color-warning)",
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: `1px solid ${C.warningBorder}`,
  },
  badgeCoach: {
    backgroundColor: C.successDim,
    color: C.success,
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: `1px solid ${C.success}`,
  },
  badgeDeleted: {
    backgroundColor: C.redDim,
    color: C.red,
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: `1px solid ${C.red}`,
  },
  entryEmail: { fontSize: "12px", color: C.text2, fontWeight: 600 },
  entryTime: {
    marginLeft: "auto",
    fontSize: "11px",
    color: C.text3,
    fontFamily: "monospace",
  },
  changes: { display: "flex", flexDirection: "column", gap: "4px" },
  changeRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    flexWrap: "wrap",
  },
  fieldName: {
    fontWeight: 700,
    color: C.text,
    minWidth: "70px",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  fromValue: {
    color: C.text3,
    textDecoration: "line-through",
    fontFamily: "monospace",
  },
  arrow: { color: C.text3, fontSize: "12px" },
  toValue: { color: C.text, fontWeight: 700, fontFamily: "monospace" },
};
